import { useEffect, useRef, useState, type TouchEvent } from "react";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { Link } from "react-router";
import type { FeedItem, Page } from "@atrium/shared";
import { api, errorMessage, qs } from "../../lib/api";
import { cn } from "../../lib/cn";
import { useInView, useIsDesktop } from "../../lib/hooks";
import { usePrefs } from "../../lib/prefs";
import { useUI, type FeedMode } from "../../lib/uiStore";
import { Segmented, EmptyState, ErrorState } from "../../components/ui/Controls";
import { Spinner } from "../../components/ui/Spinner";
import { Button, IconButton } from "../../components/ui/Button";
import { LogoMark } from "../../components/Logo";
import { IconArrowUp, IconCheck, IconChevronDown, IconCommunities, IconCompose, IconFeed, IconFire, IconRefresh, IconSearch, IconUsers } from "../../components/icons";
import { Menu, MenuContent, MenuItem, MenuTrigger } from "../../components/ui/Menu";
import { PostCard, PostCardSkeleton } from "../posts/PostCard";
import { ComposerForm } from "../posts/Composer";
import { PageFrame } from "../../app/PageFrame";
import { useAuth } from "../auth/AuthProvider";
import { usePostCountsLive, useRelationshipsLive } from "./live";

const MODES: { value: FeedMode; label: string }[] = [
  { value: "for_you", label: "For you" },
  { value: "following", label: "Following" },
  { value: "latest", label: "Latest" },
  { value: "trending", label: "Trending" },
  { value: "communities", label: "Communities" },
];
const SIGNED_IN_ONLY = new Set<FeedMode>(["following", "communities"]);
/** Chronological modes can tell when something newer has been posted. */
const CHRONOLOGICAL = new Set<FeedMode>(["following", "latest", "communities"]);

export function useFeed(mode: FeedMode) {
  const { showReposts } = usePrefs();
  return useInfiniteQuery({
    queryKey: ["feed", mode],
    queryFn: ({ pageParam }) =>
      api.get<Page<FeedItem>>(`/api/feed${qs({ mode, cursor: pageParam as string | undefined, limit: 20, reposts: mode === "following" && !showReposts ? "0" : undefined })}`),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    staleTime: 60_000,
  });
}

/** Infinite list of feed items. Hides reposts when the preference is off. */
export function PostList({ query, empty, source = "feed" }: { query: ReturnType<typeof useFeed>; empty: React.ReactNode; source?: "feed" | "profile" | "community" | "search" }) {
  const { showReposts } = usePrefs();
  const sentinel = useInView<HTMLDivElement>(() => {
    if (query.hasNextPage && !query.isFetchingNextPage) void query.fetchNextPage();
  }, !!query.hasNextPage);

  if (query.isPending) return <>{Array.from({ length: 5 }, (_, i) => <PostCardSkeleton key={i} />)}</>;
  if (query.isError) return <ErrorState message={errorMessage(query.error, "Couldn't load posts")} onRetry={() => void query.refetch()} />;
  const all = query.data.pages.flatMap((p) => p.items);
  const seen = new Set<string>();
  const items = (showReposts ? all : all.filter((i) => !i.repostedBy)).filter((i) => (seen.has(i.key) ? false : (seen.add(i.key), true)));
  if (items.length === 0 && !query.hasNextPage) return <>{empty}</>;
  return (
    <>
      {items.map((item) => (
        <PostCard key={item.key} item={item} source={source} />
      ))}
      <div ref={sentinel} className="flex h-20 items-center justify-center text-fg-3">
        {query.isFetchingNextPage ? <Spinner className="size-4" /> : !query.hasNextPage && items.length > 8 ? <span className="text-xs">You're all caught up</span> : null}
      </div>
    </>
  );
}

/** Polls the head of a chronological feed and reports whether something newer than what's shown exists. */
function useNewPosts(mode: FeedMode, topId: string | undefined, enabled: boolean) {
  const q = useQuery({
    queryKey: ["feed-head", mode],
    queryFn: () => api.get<Page<FeedItem>>(`/api/feed${qs({ mode, limit: 5 })}`),
    enabled: enabled && !!topId && CHRONOLOGICAL.has(mode),
    refetchInterval: 45_000,
    refetchIntervalInBackground: false,
    staleTime: 0,
  });
  const fresh = q.data?.items.filter((i) => topId && i.key > topId) ?? [];
  return fresh;
}

const scrollMemory = new Map<FeedMode, number>();
const PULL_TRIGGER = 64;

export function FeedPane() {
  const { feedMode, setFeedMode, openComposer } = useUI();
  const { me } = useAuth();
  const { inlineComposer } = usePrefs();
  const isDesktop = useIsDesktop();
  const scrollRef = useRef<HTMLDivElement>(null);
  const mode: FeedMode = !me && SIGNED_IN_ONLY.has(feedMode) ? "for_you" : feedMode;
  const query = useFeed(mode);
  const topId = query.data?.pages[0]?.items[0]?.key;
  const fresh = useNewPosts(mode, topId, !!me && !query.isFetching);
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const touch = useRef<{ y: number; active: boolean } | null>(null);
  usePostCountsLive();
  useRelationshipsLive();

  // Remember where each mode was scrolled to.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const top = scrollMemory.get(mode) ?? 0;
    requestAnimationFrame(() => el.scrollTo({ top }));
    const onScroll = () => scrollMemory.set(mode, el.scrollTop);
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [mode]);

  const refresh = async () => {
    setRefreshing(true);
    scrollRef.current?.scrollTo({ top: 0, behavior: "smooth" });
    try {
      await query.refetch();
    } finally {
      setRefreshing(false);
      setPull(0);
    }
  };

  const changeMode = (m: FeedMode) => {
    if (m === feedMode) return void refresh();
    setFeedMode(m);
  };

  // Pull to refresh on touch screens, from the very top of the list.
  const onTouchStart = (e: TouchEvent) => {
    const el = scrollRef.current;
    touch.current = el && el.scrollTop <= 0 && !refreshing ? { y: e.touches[0].clientY, active: true } : null;
  };
  const onTouchMove = (e: TouchEvent) => {
    if (!touch.current?.active) return;
    const dy = e.touches[0].clientY - touch.current.y;
    if (dy <= 0) return setPull(0);
    setPull(Math.min(96, dy * 0.45));
  };
  const onTouchEnd = () => {
    if (touch.current?.active && pull >= PULL_TRIGGER) void refresh();
    else setPull(0);
    touch.current = null;
  };

  const empty =
    mode === "following" ? (
      <EmptyState
        icon={IconUsers}
        title="Nothing here yet"
        description="Posts from people and communities you follow will appear here."
        action={
          <Link to="/explore">
            <Button variant="primary">Find people to follow</Button>
          </Link>
        }
      />
    ) : mode === "communities" ? (
      <EmptyState
        icon={IconCommunities}
        title="No community posts yet"
        description="Announcements and posts about communities you're in or follow appear here."
        action={
          <Link to="/explore?tab=communities">
            <Button variant="primary">Discover communities</Button>
          </Link>
        }
      />
    ) : mode === "trending" ? (
      <EmptyState icon={IconFire} title="Nothing trending right now" description="Posts with the most activity in the last two days show up here." />
    ) : mode === "for_you" && me ? (
      <EmptyState
        icon={IconFeed}
        title="Your feed is warming up"
        description="Follow people and join communities, and their posts will show up here."
        action={
          <Link to="/explore">
            <Button variant="primary">Explore</Button>
          </Link>
        }
      />
    ) : (
      <EmptyState icon={IconFeed} title="No posts yet" description="Be the first to share something." action={me ? <Button variant="primary" onClick={() => openComposer()}>Write a post</Button> : undefined} />
    );

  const modes = me ? MODES : MODES.filter((m) => !SIGNED_IN_ONLY.has(m.value));
  return (
    <PageFrame scrollRef={scrollRef}>
      <div onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd} className="relative min-h-full">
        <header className="sticky top-0 z-20 border-b border-line bg-bg/85 backdrop-blur-xl">
          <div className="flex h-14 items-center gap-3 px-3 md:px-4">
            <LogoMark className="size-6 shrink-0 text-fg md:hidden" />
            <h1 className="hidden text-lg font-semibold leading-tight text-fg md:block">Feed</h1>
            {/* Phones get a compact mode picker; five tabs don't fit beside the actions. */}
            <Menu>
              <MenuTrigger asChild>
                <button type="button" aria-label="Choose feed" className="flex h-9 min-w-0 items-center gap-1 rounded-md px-2 text-lg font-semibold text-fg hover:bg-surface md:hidden">
                  <span className="truncate">{modes.find((m) => m.value === mode)?.label ?? "Feed"}</span>
                  <IconChevronDown className="icon size-4 shrink-0 text-fg-3" aria-hidden />
                </button>
              </MenuTrigger>
              <MenuContent align="start">
                {modes.map((m) => (
                  <MenuItem key={m.value} onSelect={() => changeMode(m.value)} hint={m.value === mode ? <IconCheck className="icon size-4 text-accent" aria-label="Current" /> : undefined}>
                    {m.label}
                  </MenuItem>
                ))}
              </MenuContent>
            </Menu>
            <div className="hidden min-w-0 md:ml-auto md:block" data-swipe-ignore>
              <Segmented size="sm" ariaLabel="Feed" value={mode} onChange={changeMode} options={modes} />
            </div>
            <div className="ml-auto flex shrink-0 items-center md:ml-0">
              <span className="hidden md:inline-flex">
                <IconButton label="Refresh" icon={IconRefresh} className={cn(refreshing && "[&_svg]:animate-spin")} onClick={() => void refresh()} />
              </span>
              <Link to="/explore" aria-label="Search" className="md:hidden">
                <IconButton label="Search" icon={IconSearch} tooltip={false} />
              </Link>
              {me && (
                <span className="md:hidden">
                  <IconButton label="New post" icon={IconCompose} tooltip={false} onClick={() => openComposer()} />
                </span>
              )}
            </div>
          </div>
          {fresh.length > 0 && (
            <button
              type="button"
              onClick={() => void refresh()}
              className="new-posts-pill absolute left-1/2 top-full mt-2 flex h-8 -translate-x-1/2 items-center gap-1.5 rounded-full bg-primary px-3 text-sm font-medium text-fg-inverse shadow-pop"
            >
              <IconArrowUp className="icon size-4" aria-hidden />
              {fresh.length === 1 ? "1 new post" : `${fresh.length >= 5 ? "5+" : fresh.length} new posts`}
            </button>
          )}
        </header>
        <div
          aria-hidden={!pull && !refreshing}
          className="pull-indicator flex items-center justify-center overflow-hidden text-fg-3"
          data-refreshing={refreshing}
          style={{ height: refreshing ? 44 : pull, opacity: refreshing ? 1 : Math.min(1, pull / PULL_TRIGGER) }}
        >
          <IconRefresh className="icon size-5" style={{ transform: refreshing ? undefined : `rotate(${pull * 3}deg)` }} aria-label={refreshing ? "Refreshing" : undefined} />
        </div>
        {isDesktop && inlineComposer && me && (
          <div className="border-b border-line px-4 py-3">
            <ComposerForm inline />
          </div>
        )}
        <PostList query={query} empty={empty} />
      </div>
    </PageFrame>
  );
}
